import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { analytics, trackSignupStart, trackSignupComplete } from '../services/analytics';
import {
  trackPageView,
  trackSignupComplete as trackGA4SignupComplete,
  trackSignupError as trackGA4SignupError
} from '../services/ga4Analytics';

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessName: '',
    password: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    trackSignupStart();
    analytics.trackPageView('Signup Page');
    
    // GA4 tracking
    trackPageView('Signup Page - QuoteTier');
  }, []);

  const handleChange = e =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEmailSignup = async e => {
    e.preventDefault();
    const { name, email, businessName, password, confirmPassword } = formData;
    
    // Validation
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in the required fields', {
        toastId: 'signup-validation-error'
      });
      trackGA4SignupError('missing_required_field');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters', {
        toastId: 'password-length-error'
      });
      trackGA4SignupError('password_too_short');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match', {
        toastId: 'password-match-error'
      });
      trackGA4SignupError('password_mismatch');
      return;
    }
    
    setSubmitting(true);
    try {
      analytics.track('Signup Submitted', {
        method: 'email',
        business_name_provided: Boolean(businessName?.trim())
      });

      await authService.signUp(email, password, {
        full_name: name.trim(),
        business_name: businessName.trim()
      });
      toast.success('Account created successfully! Please check your email to verify your account.', {
        toastId: 'signup-success'
      });
      trackSignupComplete('email');
      trackGA4SignupComplete('email');

      // Fire-and-forget: ping the founder that a new signup just happened.
      // Best-effort — failures don't block the user.
      fetch('/api/hooks/new-user-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, businessName, source: 'signup' }),
      }).catch(() => {});

      // Don't redirect - let them verify email first, then login manually
      // navigate('/login?next=onboarding/welcome');
    } catch (err) {
      toast.error(err.message || 'Signup failed', {
        toastId: 'signup-error'
      });
      analytics.trackError(err, { context: 'email_signup' });
      trackGA4SignupError(err.message || 'signup_failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif&family=JetBrains+Mono:wght@400;500&display=swap');
        .font-instrument { font-family: 'Instrument Serif', serif; }
        .font-jetbrains { font-family: 'JetBrains Mono', monospace; }
      `}</style>

      <div className="min-h-screen bg-white flex font-jetbrains text-black">
        {/* left form */}
        <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 xl:px-20">
          <div className="w-full max-w-md mx-auto">
            <h1 className="font-instrument text-5xl tracking-tight mb-2">
              Create your account
            </h1>
            <p className="text-gray-600 mb-8">
              Create professional quotes that convert better
            </p>

            <form onSubmit={handleEmailSignup} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Full Name</label>
                <input
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                  placeholder="Your name"
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Business Name <span className="text-gray-500 font-normal">(optional)</span></label>
                <input
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                  placeholder="Your business, if you have one"
                  autoComplete="organization"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Email</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                  placeholder="Enter your email address"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Password</label>
                <input
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                  placeholder="Create a password (min 8 characters)"
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Confirm Password</label>
                <input
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-black rounded-lg"
                  placeholder="Confirm your password"
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {submitting ? 'Creating…' : 'Create account'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm">
              Already have an account?{' '}
              <Link to="/login" className="underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* right benefits */}
        <div className="hidden lg:flex lg:flex-1 bg-white border-l border-black">
          <div className="flex flex-col justify-center px-12 xl:px-16">
            <h2 className="font-instrument text-4xl tracking-tight mb-8">
              Why choose QuoteTier?
            </h2>
            <div className="space-y-6">
              {[
                ['67 % higher conversion', 'Interactive quotes beat PDFs'],
                ['Digital signatures', 'Instant approvals'],
                ['View tracking', 'Know when they open'],
                ['Interactive upsells', 'Clients add services']
              ].map(([title, desc]) => (
                <div key={title} className="flex items-start">
                  <div className="w-8 h-8 border border-black rounded-lg flex items-center justify-center mr-4">
                    <span className="text-xs">✓</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-0.5">{title}</h3>
                    <p className="text-sm text-gray-600">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-sm text-gray-600">
              Free forever • 3 quotes per month • No credit card required
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;
